import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import OfficialSolutionsPanel from '../components/OfficialSolutionsPanel';
import ReadOnlyPythonCode from '../components/ReadOnlyPythonCode';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import TaskStatementMeta from '../components/TaskStatementMeta';
import { useAuth } from '../context/AuthContext';
import { taskNavRef } from '../utils/taskNav';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

function groupInboxBySource(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.task?.source_name || 'Без сборника';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return Array.from(groups.entries()).map(([sourceName, rows]) => ({ sourceName, rows }));
}

function taskNumberLabel(task) {
  const num = String(task?.source_number || '').trim();
  return num || `#${task?.id}`;
}

export default function TeacherTaskInboxPage() {
  const { user } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIdParam = searchParams.get('id');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(
    selectedIdParam ? Number(selectedIdParam) : null,
  );

  const [taskFull, setTaskFull] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [attemptsData, setAttemptsData] = useState(null);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [expandedAttemptId, setExpandedAttemptId] = useState(null);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/teacher/task-inbox/', { params: { status: 'open' } });
      const list = Array.isArray(res.data?.results) ? res.data.results : [];
      setItems(list);
      return list;
    } catch {
      setItems([]);
      setError('Не удалось загрузить очередь.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (selectedIdParam) {
      const n = Number(selectedIdParam);
      if (Number.isFinite(n)) setSelectedId(n);
    }
  }, [selectedIdParam]);

  const selectedItem = useMemo(
    () => items.find((x) => x.id === selectedId) || null,
    [items, selectedId],
  );

  const selectItem = useCallback((id) => {
    setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('id', String(id));
    else next.delete('id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedItem?.task?.id) {
      setTaskFull(null);
      setAttemptsData(null);
      setNoteDraft('');
      return undefined;
    }
    setNoteDraft(selectedItem.note || '');
    const taskId = selectedItem.task.id;
    const ref = selectedItem.task.url_ref || String(taskId);
    let cancelled = false;

    setTaskLoading(true);
    setAttemptsLoading(true);
    setActionError('');
    setExpandedAttemptId(null);

    api
      .get(`/tasks/editor/${ref}/`)
      .then((res) => {
        if (!cancelled) setTaskFull(res.data);
      })
      .catch(() => {
        if (!cancelled) setTaskFull(null);
      })
      .finally(() => {
        if (!cancelled) setTaskLoading(false);
      });

    api
      .get(`/teacher/tasks/${taskId}/attempts/`)
      .then((res) => {
        if (!cancelled) setAttemptsData(res.data);
      })
      .catch(() => {
        if (!cancelled) setAttemptsData(null);
      })
      .finally(() => {
        if (!cancelled) setAttemptsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedItem?.id, selectedItem?.task?.id, selectedItem?.task?.url_ref, selectedItem?.note]);

  const groups = useMemo(() => groupInboxBySource(items), [items]);

  async function saveNote() {
    if (!selectedItem) return;
    setNoteSaving(true);
    setActionError('');
    try {
      const res = await api.patch(`/teacher/task-inbox/${selectedItem.id}/`, { note: noteDraft });
      setItems((prev) => prev.map((x) => (x.id === selectedItem.id ? { ...x, ...res.data } : x)));
    } catch {
      setActionError('Не удалось сохранить заметку.');
    } finally {
      setNoteSaving(false);
    }
  }

  async function markDone() {
    if (!selectedItem) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.patch(`/teacher/task-inbox/${selectedItem.id}/`, { status: 'done' });
      const next = await loadInbox();
      if (next.length) selectItem(next[0].id);
      else selectItem(null);
    } catch {
      setActionError('Не удалось снять с очереди.');
    } finally {
      setActionBusy(false);
    }
  }

  async function archiveTask() {
    if (!taskFull?.id && !selectedItem?.task?.id) return;
    const ref = taskNavRef(taskFull || selectedItem.task);
    if (!window.confirm('Скрыть задачу из каталога (статус «архив»)?')) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.patch(`/tasks/editor/${ref}/`, { status: 'archived' });
      if (taskFull) setTaskFull({ ...taskFull, status: 'archived' });
      setItems((prev) => prev.map((x) => (
        x.id === selectedItem?.id
          ? { ...x, task: { ...x.task, status: 'archived' } }
          : x
      )));
    } catch {
      setActionError('Не удалось архивировать задачу.');
    } finally {
      setActionBusy(false);
    }
  }

  async function deleteTask() {
    const taskId = selectedItem?.task?.id;
    if (!taskId) return;
    if (!window.confirm('Удалить задачу безвозвратно? Это нельзя отменить.')) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.post('/tasks/editor/bulk-delete/', { task_ids: [taskId] });
      const next = await loadInbox();
      if (next.length) selectItem(next[0].id);
      else selectItem(null);
      setTaskFull(null);
      setAttemptsData(null);
    } catch {
      setActionError('Не удалось удалить задачу.');
    } finally {
      setActionBusy(false);
    }
  }

  const taskRef = taskFull ? taskNavRef(taskFull) : (selectedItem?.task?.url_ref || null);
  const expectedAnswer = String(taskFull?.expected_answer ?? '').trim();

  return (
    <div className="teacher-task-inbox-page">
      <header className="page-header">
        <h1>Очередь задач</h1>
        <p className="page-subtitle text-secondary">
          Задачи, отложенные на уроке для проверки условия, эталона и попыток учеников.
        </p>
      </header>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {actionError ? <div className="alert alert-error">{actionError}</div> : null}

      <div className="task-inbox-layout">
        <aside className="task-inbox-list-panel">
          <div className="task-inbox-list-head">
            <h2 className="teacher-tools-section-title">Открытые ({items.length})</h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={loadInbox}
              disabled={loading}
            >
              Обновить
            </button>
          </div>
          {loading ? (
            <p className="text-secondary">Загрузка…</p>
          ) : items.length === 0 ? (
            <p className="text-secondary">Очередь пуста.</p>
          ) : (
            <div className="teacher-tools-issues">
              {groups.map((group) => (
                <div key={group.sourceName} className="teacher-tools-issue-group">
                  <h3 className="teacher-tools-issue-source">{group.sourceName}</h3>
                  <ul className="task-inbox-number-list">
                    {group.rows.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={`task-inbox-number-btn${selectedId === item.id ? ' is-active' : ''}`}
                          onClick={() => selectItem(item.id)}
                        >
                          {taskNumberLabel(item.task)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </aside>

        <main className="task-inbox-detail-panel">
          {!selectedItem ? (
            <p className="text-secondary">Выберите номер задачи слева.</p>
          ) : (
            <>
              <div className="task-inbox-detail-head">
                <div>
                  <h2>{taskFull?.title || selectedItem.task?.title || taskNumberLabel(selectedItem.task)}</h2>
                  <p className="text-secondary task-inbox-meta-line">
                    {selectedItem.task?.source_name
                      ? `${selectedItem.task.source_name} · №${selectedItem.task.source_number || selectedItem.task.id}`
                      : `id ${selectedItem.task?.id}`}
                    {selectedItem.student_username
                      ? ` · контекст: ${selectedItem.student_username}`
                      : ''}
                    {` · статус задачи: ${taskFull?.status || selectedItem.task?.status || '—'}`}
                  </p>
                </div>
                <div className="task-inbox-actions">
                  {taskRef ? (
                    <>
                      <Link className="btn btn-outline btn-sm" to={`/work/${taskRef}`}>
                        Как у ученика
                      </Link>
                      <Link className="btn btn-outline btn-sm" to={`/teacher/tasks?id=${selectedItem.task.id}`}>
                        Редактор
                      </Link>
                      <Link
                        className="btn btn-outline btn-sm"
                        to={`/teacher/ege-families?q=${encodeURIComponent(selectedItem.task.source_number || String(selectedItem.task.id))}`}
                      >
                        Семьи / карта
                      </Link>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={actionBusy}
                    onClick={archiveTask}
                  >
                    Скрыть (архив)
                  </button>
                  {isSiteAdmin ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={actionBusy}
                      onClick={deleteTask}
                    >
                      Удалить
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={actionBusy}
                    onClick={markDone}
                  >
                    Готово
                  </button>
                </div>
              </div>

              <section className="task-inbox-section">
                <h3>Заметка</h3>
                <textarea
                  className="form-input"
                  rows={3}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Что проверить: ответ, тесты, формулировка…"
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  disabled={noteSaving}
                  onClick={saveNote}
                >
                  {noteSaving ? 'Сохранение…' : 'Сохранить заметку'}
                </button>
              </section>

              <section className="task-inbox-section">
                <h3>Условие</h3>
                {taskLoading ? (
                  <p className="text-secondary">Загрузка условия…</p>
                ) : taskFull ? (
                  <div className="task-statement-block">
                    <TaskStatementMeta
                      source_name={taskFull.source_name}
                      source_number={taskFull.source_number}
                      source_author={taskFull.source_author}
                      year={taskFull.year}
                    />
                    <StatementAttachmentLinks attachments={taskFull.statement_attachments} variant="work" />
                    <MarkdownText className="statement-text" text={taskFull.statement || ''} />
                  </div>
                ) : (
                  <p className="text-secondary">Не удалось загрузить условие.</p>
                )}
              </section>

              <section className="task-inbox-section">
                <h3>Эталон</h3>
                {taskLoading ? (
                  <p className="text-secondary">Загрузка…</p>
                ) : taskFull ? (
                  <div className="task-inbox-etalon">
                    {expectedAnswer ? (
                      <p>
                        <strong>Ожидаемый ответ:</strong>{' '}
                        <code className="task-inbox-answer">{expectedAnswer}</code>
                      </p>
                    ) : (
                      <p className="text-secondary">Ожидаемый ответ не задан.</p>
                    )}
                    <OfficialSolutionsPanel task={taskFull} />
                  </div>
                ) : null}
              </section>

              <section className="task-inbox-section">
                <h3>Попытки учеников</h3>
                {attemptsLoading ? (
                  <p className="text-secondary">Загрузка попыток…</p>
                ) : !attemptsData?.students?.length ? (
                  <p className="text-secondary">Попыток пока нет.</p>
                ) : (
                  <div className="task-inbox-attempts">
                    {attemptsData.truncated ? (
                      <p className="text-secondary">Показаны последние {attemptsData.count} попыток.</p>
                    ) : null}
                    {attemptsData.students.map((group) => (
                      <div key={group.student_id} className="task-inbox-student-group">
                        <h4>
                          {group.student_display_name || group.student_username || `Ученик #${group.student_id}`}
                          <span className="text-secondary"> · {group.attempts.length}</span>
                        </h4>
                        <ul className="task-inbox-attempt-list">
                          {group.attempts.map((a) => (
                            <li key={a.id}>
                              <button
                                type="button"
                                className="task-inbox-attempt-toggle"
                                onClick={() => setExpandedAttemptId((cur) => (cur === a.id ? null : a.id))}
                              >
                                {a.created_at
                                  ? new Date(a.created_at).toLocaleString('ru-RU')
                                  : `#${a.id}`}
                                {' — '}
                                {SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '—'}
                              </button>
                              {expandedAttemptId === a.id ? (
                                <div className="task-inbox-attempt-body">
                                  {a.self_comment ? (
                                    <p><strong>Комментарий ученика:</strong> {a.self_comment}</p>
                                  ) : null}
                                  {a.teacher_comment ? (
                                    <p><strong>Комментарий преподавателя:</strong> {a.teacher_comment}</p>
                                  ) : null}
                                  {a.code_snapshot?.trim() ? (
                                    <ReadOnlyPythonCode value={a.code_snapshot} />
                                  ) : (
                                    <p className="text-secondary">(код пуст)</p>
                                  )}
                                  <Link
                                    className="btn btn-outline btn-xs"
                                    to={`/teacher/students/${group.student_id}`}
                                  >
                                    Карточка ученика
                                  </Link>
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
